<template>
  <overlay-side-panel
    v-bind:show="show"
    v-bind:title="$t('appUserListPanelColumnVisible.selectItemsToShow')"
    v-on="$listeners"
  >
    <div
      class="contents"
      v-bind:class="{ 'dual-section': isPositioningColumnExists }"
    >
      <!-- 全件チェックボックス -->
      <div class="all-check-wrapper">
        <label class="check all">
          <b-form-checkbox
            v-bind:checked="isAllSelected.all"
            v-bind:indeterminate="indeterminate.all"
            v-on:change="$_onChangeAll"
          />
          <span class="text" v-t="'appUserListPanelColumnVisible.all'" />
        </label>
      </div>

      <div v-if="isPositioningColumnExists" class="section-header">
        <!-- 基本情報全件チェックボックス -->
        <div
          class="all-check-wrapper"
          v-bind:style="{ width: `${standardListWidth}px` }"
        >
          <label class="check all">
            <b-form-checkbox
              v-bind:checked="isAllSelected[COLUMN_TYPE.STANDARD]"
              v-bind:indeterminate="indeterminate[COLUMN_TYPE.STANDARD]"
              v-on:change="$_onChangeTypeAll(COLUMN_TYPE.STANDARD, $event)"
            />
            <span
              class="text"
              v-t="'appUserListPanelColumnVisible.allStandardInformation'"
            />
          </label>
        </div>
        <!-- 測位情報全件チェックボックス -->
        <div class="all-check-wrapper">
          <label class="check all">
            <b-form-checkbox
              v-bind:checked="isAllSelected[COLUMN_TYPE.POSITIONING]"
              v-bind:indeterminate="indeterminate[COLUMN_TYPE.POSITIONING]"
              v-on:change="$_onChangeTypeAll(COLUMN_TYPE.POSITIONING, $event)"
            />
            <span
              class="text"
              v-t="'appUserListPanelColumnVisible.allPositioningInformation'"
            />
          </label>
        </div>
      </div>

      <div class="check-list-container">
        <!-- 基本情報リスト -->
        <ul ref="standardList" class="column-check-list">
          <li
            v-for="column in columnDefsGroupedByType[COLUMN_TYPE.STANDARD]"
            v-bind:key="column.colId"
            class="check-item"
          >
            <label class="check">
              <b-form-checkbox
                v-bind:checked="visible[column.colId]"
                v-on:change="$_onChangeEach(column.colId, $event)"
              />
              <span class="text">{{ column.headerName }}</span>
            </label>
          </li>
        </ul>
        <!-- 測位情報リスト -->
        <ul v-if="isPositioningColumnExists" class="column-check-list">
          <li
            v-for="column in columnDefsGroupedByType[COLUMN_TYPE.POSITIONING]"
            v-bind:key="column.colId"
            class="check-item"
          >
            <label class="check">
              <b-form-checkbox
                v-bind:checked="visible[column.colId]"
                v-on:change="$_onChangeEach(column.colId, $event)"
              />
              <span class="text">{{ column.headerName }}</span>
            </label>
          </li>
        </ul>
      </div>
    </div>
  </overlay-side-panel>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import OverlaySidePanel from '@/components/AppOverlaySidePanel.vue'
import {
  COLUMN_ID,
  COLUMN_TYPE,
  COLUMN_ID_TYPE_RELATION,
} from '@/composables/user/UseUserListColumnDefinition'

// 基本情報カラムの表示順定義
const STANDARD_COLUMN_ORDERS = [
  COLUMN_ID.ID,
  COLUMN_ID.USER_NAME,
  COLUMN_ID.USER_NAME_KANA,
  COLUMN_ID.ROLE,
  COLUMN_ID.MANAGER,
  COLUMN_ID.ORGANIZATION,
  COLUMN_ID.TEL,
  COLUMN_ID.EMAIL,
  COLUMN_ID.REMARKS,
  COLUMN_ID.LAST_LOGIN_AT,
  COLUMN_ID.CREATE_USER_NAME,
  COLUMN_ID.CREATE_AT,
  COLUMN_ID.UPDATE_USER_NAME,
  COLUMN_ID.UPDATE_AT,
]

/**
 * 基本情報カラムのソート
 * * STANDARD_COLUMN_ORDERSに存在するカラムは定義通りにソート
 * * STANDARD_COLUMN_ORDERSに存在しないカラムはSTANDARD_COLUMN_ORDERSの後に入力順のまま移動
 * @param {ColumnDef} a
 * @param {ColumnDef} b
 * @returns {number}
 */
const sortStandardColumns = (a, b) => {
  const indexA = STANDARD_COLUMN_ORDERS.findIndex((id) => id === a.colId)
  const indexB = STANDARD_COLUMN_ORDERS.findIndex((id) => id === b.colId)
  if (indexA === -1 && indexA === indexB) {
    return 0
  } else if (indexA === -1) {
    return 1
  } else if (indexB === -1) {
    return -1
  } else {
    return indexA - indexB
  }
}

export default {
  name: 'AppUserListPanelColumnVisible',
  components: {
    OverlaySidePanel,
  },
  model: {
    prop: 'show',
    event: 'change:show',
  },
  props: {
    /**
     * カラム定義
     */
    columnDefs: {
      type: Array,
      default: () => [],
    },
    /**
     * 各カラムの表示状態
     */
    visible: {
      type: Object,
      default: () => {},
    },
    /**
     * パネル表示フラグ
     * @model
     */
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * カラム表示状態変更イベント
     * 表示状態の差分のみをペイロードとして発行する
     * @param {{ colId: string; visible: boolean; }[]} 表示状態変更情報
     */
    'change:visible',
    /**
     * パネル表示状態変更イベント(v-model更新用)
     * @param {boolean} パネル表示状態
     */
    'change:show',
  ],
  setup() {
    /**
     * 基本情報の全件チェックボックスの幅をアイテムの幅に合わせる
     */
    /**
     * @type {ResizeObserver}
     */
    let observer = null
    /**
     * 基本情報アイテム
     * @type {Ref.<HTMLElement>}
     */
    const standardList = ref(null)
    /**
     * 基本情報アイテムの幅(px)
     * @type {Ref.<number>}
     */
    const standardListWidth = ref(0)
    /**
     * 基本情報アイテムの幅を更新
     */
    const updateStandardListWidth = () => {
      const rect = standardList.value?.getBoundingClientRect()
      standardListWidth.value = rect.width
    }

    onMounted(() => {
      observer = new ResizeObserver(updateStandardListWidth)
      observer.observe(standardList.value)
    })
    onBeforeUnmount(() => {
      observer?.disconnect()
    })

    return {
      COLUMN_TYPE,
      standardList,
      standardListWidth,
    }
  },
  computed: {
    /**
     * 種類で区分したカラム定義
     * @type {Object}
     */
    columnDefsGroupedByType: function () {
      const defs = this.columnDefs.reduce(
        (result, def) => {
          const type =
            COLUMN_ID_TYPE_RELATION[def.colId] || COLUMN_TYPE.STANDARD
          result[type]?.push(def)
          return result
        },
        {
          [COLUMN_TYPE.STANDARD]: [],
          [COLUMN_TYPE.POSITIONING]: [],
        }
      )
      defs[COLUMN_TYPE.STANDARD].sort(sortStandardColumns)
      return defs
    },
    /**
     * 測位情報のカラムが存在する場合はtrue
     * @type {Boolean}
     */
    isPositioningColumnExists: function () {
      return !!this.columnDefsGroupedByType[COLUMN_TYPE.POSITIONING].length
    },
    /**
     * visible:true のカラム数
     * @type {Object}
     */
    visibleCount: function () {
      const entries = Object.values(COLUMN_TYPE).map((type) => [
        type,
        this.columnDefsGroupedByType[type].reduce(
          (count, def) => count + +this.visible[def.colId],
          0
        ),
      ])
      return {
        ...Object.fromEntries(entries),
        all: entries.reduce((sum, [, count]) => sum + count, 0),
      }
    },
    /**
     * すべて選択されている場合はtrue
     * @type {Object}
     */
    isAllSelected: function () {
      const entries = Object.values(COLUMN_TYPE).map((type) => [
        type,
        this.columnDefsGroupedByType[type].length === this.visibleCount[type],
      ])
      return {
        ...Object.fromEntries(entries),
        all: entries.every(([, value]) => value),
      }
    },
    /**
     * 中間状態
     * @type {Object}
     */
    indeterminate: function () {
      const keys = Object.keys(this.isAllSelected)
      return Object.fromEntries(
        keys.map((type) => [
          type,
          this.visibleCount[type] > 0 && !this.isAllSelected[type],
        ])
      )
    },
  },
  methods: {
    /**
     * 表示状態の変化分を取得する
     * @param {ColDef[]} columnDefs 検査対象カラム定義
     * @param {boolean} value 変更後の visible 値
     * @returns {Object[]} 変化データ
     */
    $_getVisibleChanges: function (columnDefs, value) {
      return columnDefs
        .filter(({ colId }) => this.visible[colId] !== value)
        .map(({ colId }) => ({ colId, visible: value }))
    },
    /**
     * 全選択チェッククリック時処理
     * @param {boolean} value 変更値
     */
    $_onChangeAll: function (value) {
      this.$emit(
        'change:visible',
        this.$_getVisibleChanges(this.columnDefs, value)
      )
    },
    /**
     * 種類のすべてチェックボックスクリック時処理
     * @param {string} type 種類
     * @param {boolean} value 変更値
     */
    $_onChangeTypeAll: function (type, value) {
      this.$emit(
        'change:visible',
        this.$_getVisibleChanges(this.columnDefsGroupedByType[type], value)
      )
    },
    /**
     * 各項目チェックボックスクリック時処理
     * @param {string} colId カラムID
     * @param {boolean} value 変更値
     */
    $_onChangeEach: function (colId, value) {
      this.$emit('change:visible', [{ colId, visible: value }])
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../assets/scss/mixin' as mixin;

/**
 * 部品汎用スタイル
 */
.check {
  width: fit-content;
  margin: 0;
  padding: 8px 0;
  cursor: pointer;

  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;

  ::v-deep .custom-control {
    height: 1rem;
    min-height: 1rem;
    padding-left: 1rem;

    .custom-control-input {
      top: 0rem;
      height: 1rem;
    }

    .custom-control-label {
      cursor: pointer;
      &::before {
        top: 0rem;
        left: -1rem;
      }
      &::after {
        top: 0rem;
        left: -1rem;
      }
    }
  }

  &.all {
    .text {
      font-weight: $font-weight-bold;
    }
  }
}

.text {
  color: $text-color;
  font-size: $font-size-middle;
  line-height: 1;
  text-align: left;
}

/**
 * 配置関連スタイル
 */
.contents {
  width: 100%;
  height: 100%;
  padding: 0 8px 8px;
  overflow: hidden;
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr);

  &.dual-section {
    grid-template-rows: max-content max-content minmax(0, 1fr);

    & > .all-check-wrapper {
      margin-bottom: 8px;
    }
  }

  // カテゴリが1つしかないときは全件チェックボックスに下線をつける
  &:not(.dual-section) {
    & > .all-check-wrapper {
      margin-bottom: 4px;
      border-bottom: 1px solid $border-color;
    }
  }
}

.section-header {
  width: 100%;
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 16px;
  margin-bottom: 4px;

  .all-check-wrapper {
    border-bottom: 1px solid $border-color;
  }
}

.check-list-container {
  height: 100%;
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 16px;
  overflow-y: auto;
  scrollbar-gutter: stable;
  @include mixin.scrollbar;

  .column-check-list {
    min-width: 10rem;
    height: 100%;
    margin: 0;
    padding: 0;
    list-style-type: none;

    .check-item {
      width: fit-content;
      margin: 0;
    }
  }
}
</style>
